import { PropertySearchFilters, MAX_PROPERTY_PAGE_SIZE } from "../swr/properties";
import { Amenity } from "../types/amenity";
import { PropertyTypeEnum } from "../types/propertyType";
import { statesForSearch } from "../types/states";

// Option lists use the API's enum member names as values so they can be sent
// to /api/Properties/Search as-is.
export const PROPERTY_TYPE_OPTIONS = Object.entries(PropertyTypeEnum).map(
  ([value, label]) => ({ value, label }),
);

export const PROPERTY_STATUS_OPTIONS = [
  { value: "Rent", label: "Rent" },
  { value: "Sale", label: "Sale" },
  { value: "RentToOwn", label: "Rent To Own" },
  { value: "Mortgage", label: "Mortgage" },
];

// "EquippedKitchen" -> "Equipped Kitchen", "24hrElectricity" -> "24hr Electricity"
export const AMENITY_OPTIONS = Object.entries(Amenity).map(([value, label]) => ({
  value,
  label: label.replace(/([a-z])([A-Z])/g, "$1 $2"),
}));

// The API's States enum has no Abuja (statesForSearch lists it last).
const SEARCHABLE_STATES = statesForSearch.filter((s) => s !== "Abuja");

export const PAGE_SIZE_OPTIONS = [12, 20, 30, MAX_PROPERTY_PAGE_SIZE];
const DEFAULT_PAGE_SIZE = PAGE_SIZE_OPTIONS[0];

const pick = (value: string | null, allowed: string[]) => {
  if (!value) return undefined;
  const compact = value.replace(/\s/g, "").toLowerCase();
  return allowed.find((a) => a.toLowerCase() === compact);
};

const toNumber = (value: string | null) => {
  if (value === null || value.trim() === "") return undefined;
  const n = Number(value);
  return Number.isFinite(n) && n >= 0 ? n : undefined;
};

export interface PropertySearchState {
  filters: PropertySearchFilters;
  page: number;
  pageSize: number;
  // A state was asked for that the API can't search (e.g. Abuja).
  unsupportedState: boolean;
}

// Reads /properties?type=&status=&state=&city=&priceFrom=&priceTo=&amenities=&page=&pageSize=
// Also accepts the hero search form's values ("Akwa Ibom", "Rent", ...).
export const parsePropertySearch = (search: string): PropertySearchState => {
  const q = new URLSearchParams(search);
  const state = pick(q.get("state"), SEARCHABLE_STATES);
  const amenityKeys = AMENITY_OPTIONS.map((o) => o.value);
  const amenities = (q.get("amenities") || "")
    .split(",")
    .map((a) => pick(a, amenityKeys))
    .filter((a): a is string => !!a);
  const pageSize = toNumber(q.get("pageSize"));

  return {
    filters: {
      type: pick(q.get("type"), PROPERTY_TYPE_OPTIONS.map((o) => o.value)),
      status: pick(q.get("status"), PROPERTY_STATUS_OPTIONS.map((o) => o.value)),
      state,
      city: q.get("city")?.trim() || undefined,
      priceFrom: toNumber(q.get("priceFrom")),
      priceTo: toNumber(q.get("priceTo")),
      amenities: amenities.length ? amenities : undefined,
    },
    page: Math.max(1, Math.floor(toNumber(q.get("page")) || 1)),
    pageSize:
      pageSize && PAGE_SIZE_OPTIONS.includes(pageSize) ? pageSize : DEFAULT_PAGE_SIZE,
    unsupportedState: !!q.get("state") && !state,
  };
};

export const stringifyPropertySearch = ({
  filters,
  page,
  pageSize,
}: Omit<PropertySearchState, "unsupportedState">) => {
  const q = new URLSearchParams();
  const { type, status, state, city, priceFrom, priceTo, amenities } = filters;
  if (type) q.set("type", type);
  if (status) q.set("status", status);
  if (state) q.set("state", state);
  if (city) q.set("city", city);
  if (priceFrom !== undefined) q.set("priceFrom", String(priceFrom));
  if (priceTo !== undefined) q.set("priceTo", String(priceTo));
  if (amenities?.length) q.set("amenities", amenities.join(","));
  if (page > 1) q.set("page", String(page));
  if (pageSize !== DEFAULT_PAGE_SIZE) q.set("pageSize", String(pageSize));
  const s = q.toString();
  return s ? `?${s}` : "";
};
